import React, { useEffect, useRef, useMemo, useCallback, useState } from "react"
import { FormattedMessage } from "react-intl"
import { Card, TreeSelect } from "antd"
import uniq from "lodash/uniq"
import isNil from "lodash/isNil"
import without from "lodash/without"
import { DefaultOptionType } from "rc-tree-select/lib/TreeSelect"

import { useSelector } from "react-redux"
import selectors from "src/store/selectors"
import useAction from "src/hooks/useAction"
import actions from "src/store/actions"

import { OPF_DOCUMENT_TYPES, OPF_ITEM_TYPES, ROLES } from "src/globals"
import { getDocumentSuperType } from "src/components/helpers"
import unflattenTree from "src/store/utils/unflattenTree"
import FieldWrapper from "src/modals/shared/FieldWrapper"
import filterGlobalEmptyCategories from "src/store/utils/filterGlobalEmptyCategories"
import { flattenTree } from "src/store/utils/flattenTree"
import { prepareTreeData } from "src/store/utils/prepareTreeData"
import { Document, OpfBase, RelatedDocumentItem } from "src/types"
import { useProcessItems } from "./useProcessItems"

type OPFID = string

interface Props {
  relatedDocuments: RelatedDocumentItem[]
  relatedProcesses: RelatedDocumentItem[]
  disabledRelatedDocuments: Record<string, true>
  disabledRelatedProcesses: Record<string, true>
  initialProcessesSlugs: string[]
  onDocumentsChange: (documents: RelatedDocumentItem[]) => void
  onProcessesChange: (processes: RelatedDocumentItem[]) => void
}

const { DOCUMENT, DOCUMENT_SUBCATEGORY } = OPF_DOCUMENT_TYPES
const { PROCEDURE_CATEGORY, PROCESS_CATEGORY, PROCESS, PROCEDURE } = OPF_ITEM_TYPES

const POLICY_CATEGORY_TYPES = [
  OPF_DOCUMENT_TYPES.POLICIES_STANDARDS_CATEGORY,
  OPF_DOCUMENT_TYPES.POLICIES_STANDARDS_SUBCATEGORY,
]
const PROCESS_CATEGORY_TYPES = [PROCESS_CATEGORY, PROCEDURE_CATEGORY]
const PROCESS_TYPES = [PROCESS, PROCEDURE]

const GLOBAL_TYPE = true
const LOCAL_TYPE = false

const TREE_SELECT_STYLE = { width: "100%" }
const FIELD_NAMES_CONFIG = { title: "title", value: "opfId", children: "children" }

const filterMissingValues = (opfIds: OPFID[], allOpfElements: any[]): OPFID[] => {
  if (!Array.isArray(opfIds)) return []
  return opfIds.filter((opfId) => allOpfElements.some((opfElement) => opfElement.opfId === opfId))
}

const toRelatedItems = (
  opfIds: OPFID[],
  source: { opfId: string; slug: string }[]
): RelatedDocumentItem[] =>
  opfIds.flatMap((opfId) => {
    const match = source.find((item) => item.opfId === opfId)
    return match ? [{ opfId, slug: match.slug }] : []
  })

const filterTreeNode = (search: string, item: any, fieldName = "name"): boolean => {
  return item[fieldName]?.toLowerCase().includes(search.toLowerCase()) ?? false
}

const filterDocumentsByType = (
  documents: any[],
  global: boolean,
  categoryTypes: string[]
): any[] => {
  return documents.filter((item) => item.global === global || categoryTypes.includes(item.type))
}

const RelatedDocumentsAndProcessesFields: React.FC<Props> = ({
  relatedDocuments,
  relatedProcesses,
  disabledRelatedDocuments,
  disabledRelatedProcesses,
  onDocumentsChange,
  onProcessesChange,
  initialProcessesSlugs,
}) => {
  const { processItems, loadProcessItem, isLoading } = useProcessItems(initialProcessesSlugs)
  const flatTreeDocumentsRequest = useRef<{ cancel?: () => void } | null>(null)
  const [isPoliciesLoading, setIsPoliciesLoading] = useState(true)

  const documents = useSelector(
    selectors.documentsSelectors.getDocumentsForCurrentTree
  ) as Document[]
  const user = useSelector(selectors.userSelectors.getUser)
  const fetchFlatTreeDocuments = useAction(actions.documentsActions.fetchFlatTreeDocuments)

  const isGlobalUser = useMemo(
    () => user.currentRole.name === ROLES.GLOBAL_POLICIES_STANDARDS_OWNER,
    [user.currentRole.name]
  )

  // Memoized document processing
  const { localDocumentsAsTreeNodes, globalDocumentsAsTreeNodes, localDocuments, globalDocuments } =
    useMemo(() => {
      const localDocsAndCategories = filterDocumentsByType(
        documents,
        LOCAL_TYPE,
        POLICY_CATEGORY_TYPES
      )
      const globalDocsAndCategories = documents.filter((item) => item.global === GLOBAL_TYPE)

      const isDisabled = (item: OpfBase) => Boolean(disabledRelatedDocuments?.[item.opfId])
      const isLeaf = (item: OpfBase) => !POLICY_CATEGORY_TYPES.includes(item.type)

      const localTreeNodes = prepareTreeData(
        filterGlobalEmptyCategories(unflattenTree(localDocsAndCategories), LOCAL_TYPE),
        POLICY_CATEGORY_TYPES,
        isDisabled,
        isLeaf
      )
      const globalTreeNodes = prepareTreeData(
        filterGlobalEmptyCategories(unflattenTree(globalDocsAndCategories), GLOBAL_TYPE),
        POLICY_CATEGORY_TYPES,
        isDisabled,
        isLeaf
      )

      return {
        localDocumentsAsTreeNodes: localTreeNodes,
        globalDocumentsAsTreeNodes: globalTreeNodes,
        localDocuments: flattenTree(localTreeNodes),
        globalDocuments: flattenTree(globalTreeNodes),
      }
    }, [documents])

  // Memoized process processing
  const { localProcessesAsTreeNodes, globalProcessesAsTreeNodes, localProcess, globalProcess } =
    useMemo(() => {
      const localProcAndCategories = processItems.filter(
        (item: OpfBase) => item.global === LOCAL_TYPE || PROCESS_CATEGORY_TYPES.includes(item.type)
      )
      const globalProcAndCategories = processItems.filter(
        (item: OpfBase) => item.global === GLOBAL_TYPE
      )

      const isDisabled = (item: OpfBase) =>
        Boolean(disabledRelatedProcesses?.[item.opfId]) ||
        PROCESS_CATEGORY_TYPES.includes(item.type)

      const isLeaf = (item: OpfBase) => item.type === PROCEDURE

      const localTreeNodes = prepareTreeData(
        filterGlobalEmptyCategories(unflattenTree(localProcAndCategories), LOCAL_TYPE),
        PROCESS_CATEGORY_TYPES,
        isDisabled,
        isLeaf
      )
      const globalTreeNodes = prepareTreeData(
        filterGlobalEmptyCategories(unflattenTree(globalProcAndCategories), GLOBAL_TYPE),
        PROCESS_CATEGORY_TYPES,
        isDisabled,
        isLeaf
      )

      return {
        localProcessesAsTreeNodes: localTreeNodes,
        globalProcessesAsTreeNodes: globalTreeNodes,
        localProcess: flattenTree(localTreeNodes),
        globalProcess: flattenTree(globalTreeNodes),
      }
    }, [processItems])

  useEffect(() => {
    flatTreeDocumentsRequest.current = fetchFlatTreeDocuments()
      .then(() => {
        flatTreeDocumentsRequest.current = null
        setIsPoliciesLoading(false)
      })
      .catch(() => {
        setIsPoliciesLoading(false)
        flatTreeDocumentsRequest.current = null
      })

    return () => flatTreeDocumentsRequest.current?.cancel?.()
  }, [])

  const expandRelatedDocuments = useCallback(
    (opfId: OPFID, isGlobal: boolean): Document[] => {
      const documentChildren = documents.filter(
        (document: Document) =>
          (document.opfId === opfId || document.parentId === opfId) &&
          getDocumentSuperType(document.type) === DOCUMENT &&
          document.global === isGlobal
      )
      const categoryChildren = documents.filter(
        (document: Document) =>
          document.parentId === opfId &&
          getDocumentSuperType(document.type) === DOCUMENT_SUBCATEGORY &&
          document.global === isGlobal
      )
      const expandedCategoryDocuments: Document[] = categoryChildren.flatMap(({ opfId }) =>
        expandRelatedDocuments(opfId, isGlobal)
      )

      return [...documentChildren, ...expandedCategoryDocuments]
    },
    [documents]
  )

  const expandRelatedProcess = useCallback(
    (opfId: OPFID, isGlobal: boolean): OpfBase[] => {
      const documentChildren = processItems.filter(
        (document: OpfBase) =>
          (document.opfId === opfId || document.parentId === opfId) &&
          PROCESS_TYPES.includes(document.type) &&
          document.global === isGlobal
      )

      const categoryChildren = processItems.filter(
        (document: OpfBase) =>
          document.parentId === opfId &&
          PROCESS_CATEGORY_TYPES.includes(document.type) &&
          document.global === isGlobal
      )
      const expandedCategoryDocuments: OpfBase[] = categoryChildren.flatMap(({ opfId }) =>
        expandRelatedProcess(opfId, isGlobal)
      )

      return [...documentChildren, ...expandedCategoryDocuments]
    },
    [processItems]
  )

  const onRelatedDocumentsChange = useCallback(
    (opfIds: OPFID[], otherDocuments: OPFID[], isGlobal: boolean) => {
      const validOpfIds = (opfIds ?? []).filter((v) => !isNil(v))
      const relatedDocs = validOpfIds.flatMap((opfId) => expandRelatedDocuments(opfId, isGlobal))
      const relatedOpfIds = relatedDocs.map(({ opfId }) => opfId)

      onDocumentsChange(toRelatedItems(uniq([...otherDocuments, ...relatedOpfIds]), documents))
    },
    [expandRelatedDocuments, onDocumentsChange, documents]
  )

  // Memoized opfId lists derived from the slug+opfId props
  const relatedDocumentOpfIds = useMemo(
    () => relatedDocuments.map(({ opfId }) => opfId),
    [relatedDocuments]
  )
  const relatedProcessOpfIds = useMemo(
    () => relatedProcesses.map(({ opfId }) => opfId),
    [relatedProcesses]
  )

  // Memoized values
  const globalDocumentsValues = useMemo(
    () => filterMissingValues(relatedDocumentOpfIds, globalDocuments),
    [relatedDocumentOpfIds, globalDocuments]
  )
  const localDocumentsValues = useMemo(
    () => filterMissingValues(relatedDocumentOpfIds, localDocuments),
    [relatedDocumentOpfIds, localDocuments]
  )
  const localProcessesValues = useMemo(
    () => filterMissingValues(relatedProcessOpfIds, localProcess),
    [relatedProcessOpfIds, localProcess]
  )
  const globalProcessesValues = useMemo(
    () => filterMissingValues(relatedProcessOpfIds, globalProcess),
    [relatedProcessOpfIds, globalProcess]
  )

  // Memoized callbacks for TreeSelect onChange handlers
  const handleLocalDocumentsChange = useCallback(
    (values: OPFID[]) => onRelatedDocumentsChange(values, globalDocumentsValues, LOCAL_TYPE),
    [onRelatedDocumentsChange, globalDocumentsValues]
  )

  const handleGlobalDocumentsChange = useCallback(
    (values: OPFID[]) => onRelatedDocumentsChange(values, localDocumentsValues, GLOBAL_TYPE),
    [onRelatedDocumentsChange, localDocumentsValues]
  )

  const handleLocalProcessSelect = useCallback(
    (value: OPFID) =>
      onProcessesChange(
        toRelatedItems(
          uniq([...localProcessesValues, value, ...globalProcessesValues]),
          processItems
        )
      ),
    [onProcessesChange, localProcessesValues, globalProcessesValues, processItems]
  )

  const handleGlobalProcessSelect = useCallback(
    (value: OPFID) =>
      onProcessesChange(
        toRelatedItems(
          uniq([...localProcessesValues, ...globalProcessesValues, value]),
          processItems
        )
      ),
    [onProcessesChange, localProcessesValues, globalProcessesValues, processItems]
  )

  const handleProcessDeselect = useCallback(
    (value: OPFID) =>
      onProcessesChange(
        toRelatedItems(
          without(uniq([...localProcessesValues, ...globalProcessesValues]), value),
          processItems
        )
      ),
    [onProcessesChange, localProcessesValues, globalProcessesValues, processItems]
  )

  const filterDocumentTreeNode = useCallback(
    (search: string, item: DefaultOptionType) => filterTreeNode(search, item, "name"),
    []
  )

  return (
    <div className="fade-in">
      <br />
      <Card title={<FormattedMessage id="form.step.relations.intro.title" />} bordered={false}>
        <FormattedMessage id="form.step.relations.intro.description" />
      </Card>
      {!isGlobalUser && (
        <FieldWrapper name="localRelatedDocuments" field={{ value: relatedDocuments }}>
          <TreeSelect
            onChange={handleLocalDocumentsChange}
            placeholder="Select Related Local Policies - Standards"
            multiple
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_CHILD}
            loading={isPoliciesLoading}
            style={TREE_SELECT_STYLE}
            treeData={localDocumentsAsTreeNodes}
            disabled={isPoliciesLoading}
            filterTreeNode={filterDocumentTreeNode}
            value={localDocumentsValues}
            treeDefaultExpandedKeys={localDocumentsValues}
          />
        </FieldWrapper>
      )}
      <FieldWrapper name="relatedDocuments" field={{ value: relatedDocuments }}>
        <TreeSelect
          onChange={handleGlobalDocumentsChange}
          placeholder="Select Related Global Policies - Standards"
          multiple
          treeCheckable
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          loading={isPoliciesLoading}
          disabled={isPoliciesLoading}
          style={TREE_SELECT_STYLE}
          treeData={globalDocumentsAsTreeNodes}
          filterTreeNode={filterDocumentTreeNode}
          value={globalDocumentsValues}
          treeDefaultExpandedKeys={globalDocumentsValues}
        />
      </FieldWrapper>
      {!isGlobalUser && (
        <FieldWrapper name="localRelatedProcesses" field={{ value: relatedProcesses }}>
          <TreeSelect
            onSelect={handleLocalProcessSelect}
            onDeselect={handleProcessDeselect}
            placeholder="Select Related Local Processes"
            multiple
            treeCheckable
            fieldNames={FIELD_NAMES_CONFIG}
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            loading={isLoading}
            disabled={isLoading}
            style={TREE_SELECT_STYLE}
            treeCheckStrictly
            treeData={localProcessesAsTreeNodes}
            value={localProcessesValues}
            loadData={({ slug }) => loadProcessItem(slug)}
            showSearch={false}
            treeDefaultExpandedKeys={localProcessesValues}
          />
        </FieldWrapper>
      )}
      <FieldWrapper name="relatedProcesses" field={{ value: relatedProcesses }}>
        <TreeSelect
          onSelect={handleGlobalProcessSelect}
          onDeselect={handleProcessDeselect}
          placeholder="Select Related Global Processes"
          multiple
          treeCheckable
          fieldNames={FIELD_NAMES_CONFIG}
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          loading={isLoading}
          disabled={isLoading}
          style={TREE_SELECT_STYLE}
          treeCheckStrictly
          treeData={globalProcessesAsTreeNodes}
          value={globalProcessesValues}
          loadData={({ slug }) => loadProcessItem(slug)}
          showSearch={false}
          treeDefaultExpandedKeys={globalProcessesValues}
        />
      </FieldWrapper>
    </div>
  )
}

export default RelatedDocumentsAndProcessesFields
